import { useId, type ReactNode } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { MeetingFormat } from '../../core/models/meeting-format';
import type { PublicConsultationType } from '../../core/models/public-consultation-type';
import { InfoIcon, MapPinIcon, MonitorIcon } from './booking-icons';
import { CityPicker } from './city-picker';

interface ServiceStepProps {
    /** Only the tab's own services — the page splits them, so this list is already one price band. */
    types: readonly PublicConsultationType[];
    loading: boolean;
    paid: boolean;
    selected: PublicConsultationType | null;
    format: MeetingFormat;
    /** False for a free service, which is held in an office only. */
    onlineOffered: boolean;
    /** The office's key, asked only for a meeting in one. */
    city: string | null;
    errors: Record<string, TranslationKey>;
    onTabChange: (paid: boolean) => void;
    onSelect: (typeId: string | null) => void;
    onFormatChange: (format: MeetingFormat) => void;
    onCityChange: (city: string | null) => void;
    /** The wizard's error banner and button row, which the live page draws inside the card. */
    children: ReactNode;
}

/**
 * Step one: which consultation, and how it happens.
 *
 * **The free and paid tabs are derived, not declared.** The live page hard-codes two lists of
 * services in its own JavaScript; here `price === null` is what free means, and the tabs are a view
 * of whatever the server sent in whatever order it sent it. A service added in the admin appears
 * under the right tab without anybody editing this file.
 *
 * **A free service is held in an office only.** The online card is not offered for one, and the
 * server refuses a free service booked online, so this is presentation of that rule rather than
 * the rule itself.
 *
 * **An office meeting asks for the office here**, not beside the contact details: the next step
 * offers only the consultants who work in it.
 */
export function ServiceStep({
    types,
    loading,
    paid,
    selected,
    format,
    onlineOffered,
    city,
    errors,
    onTabChange,
    onSelect,
    onFormatChange,
    onCityChange,
    children
}: ServiceStepProps) {
    const { t, language } = useTranslation();
    const russian = language === 'ru';
    const selectId = useId();
    const formatLabelId = useId();
    const error = errors['consultationTypeId'];
    const description = selected === null ? null : russian ? selected.descriptionRu : selected.descriptionKk;

    return (
        <div className="card">
            <span className="card-lbl">{t('booking.s1.step')}</span>
            <h1 className="card-title">{t('booking.s1.title')}</h1>

            <div className="type-tabs" role="tablist" aria-label={t('booking.s1.select')}>
                <button
                    type="button"
                    role="tab"
                    aria-selected={!paid}
                    className={paid ? 'type-tab' : 'type-tab active'}
                    onClick={() => onTabChange(false)}
                >
                    <span>{t('booking.free')}</span> <span className="ttag tag-free">FREE</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={paid}
                    className={paid ? 'type-tab active' : 'type-tab'}
                    onClick={() => onTabChange(true)}
                >
                    <span>{t('booking.s1.paid')}</span> <span className="ttag tag-paid">PAID</span>
                </button>
            </div>

            <label className="fl" htmlFor={selectId}>
                <span>{t('booking.s1.select')}</span> <span className="req">*</span>
            </label>
            <div className="sw">
                <select
                    id={selectId}
                    className={error === undefined ? undefined : 'err'}
                    value={selected?.id ?? ''}
                    disabled={loading}
                    aria-invalid={error !== undefined}
                    onChange={(event) => onSelect(event.target.value === '' ? null : event.target.value)}
                >
                    <option value="">{loading ? t('booking.loading_services') : t('booking.s1.placeholder')}</option>
                    {types.map((type) => (
                        <option key={type.id} value={type.id}>
                            {russian ? type.nameRu : type.nameKk}
                        </option>
                    ))}
                </select>
            </div>

            {error !== undefined && (
                <p className="fe" role="alert">
                    {t(error)}
                </p>
            )}

            {selected !== null && (
                <div className="desc-box">
                    <div className="desc-ic">
                        <InfoIcon />
                    </div>
                    <div>
                        {description !== null && <p className="desc-txt">{description}</p>}
                        <span className={selected.price === null ? 'pchip p-free' : 'pchip p-paid'}>
                            {selected.price === null ? t('booking.free') : formatTenge(selected.price)} ·{' '}
                            {selected.durationMinutes} {t('booking.minutes')}
                        </span>
                    </div>
                </div>
            )}

            {selected !== null && (
                <div className="format-section" role="group" aria-labelledby={formatLabelId}>
                    <p className="format-lbl" id={formatLabelId}>
                        <span>{t('booking.step.format')}</span> <span className="req">*</span>
                    </p>

                    <div className={onlineOffered ? 'format-cards' : 'format-cards format-cards-single'}>
                        <FormatCard
                            format="InPerson"
                            current={format}
                            className="fc-offline"
                            selectedClassName="sel-offline"
                            title={t('booking.format.in_person')}
                            description={t('booking.s1.offline_desc')}
                            icon={<MapPinIcon />}
                            onSelect={onFormatChange}
                        />
                        {onlineOffered && (
                            <FormatCard
                                format="Online"
                                current={format}
                                className="fc-online"
                                selectedClassName="sel-online"
                                title={t('booking.format.online')}
                                description={t('booking.s1.online_desc')}
                                icon={<MonitorIcon />}
                                onSelect={onFormatChange}
                            />
                        )}
                    </div>

                    {!onlineOffered && <p className="desc-txt">{t('booking.s1.free_in_person')}</p>}

                    {format === 'Online' && (
                        <p className="skip-notice">
                            <MonitorIcon />
                            {t('booking.s1.skip_notice')}
                        </p>
                    )}

                    {errors['meetingFormat'] !== undefined && (
                        <p className="fe" role="alert">
                            {t(errors['meetingFormat'])}
                        </p>
                    )}

                    {format === 'InPerson' && <CityPicker selected={city} error={errors['city']} onChange={onCityChange} />}
                </div>
            )}

            {children}
        </div>
    );
}

/**
 * One of the two format cards.
 *
 * A `<button>` with `aria-pressed`, where the live page uses a `<div onclick>` — which is
 * unreachable from a keyboard and announces itself as nothing at all. The two colour schemes are
 * the live page's own: emerald for the office, blue for online, so the choice is legible before it
 * is read.
 */
function FormatCard({
    format,
    current,
    className,
    selectedClassName,
    title,
    description,
    icon,
    onSelect
}: {
    format: MeetingFormat;
    current: MeetingFormat;
    className: string;
    selectedClassName: string;
    title: string;
    description: string;
    icon: ReactNode;
    onSelect: (format: MeetingFormat) => void;
}) {
    const { t } = useTranslation();
    const chosen = current === format;

    return (
        <button
            type="button"
            aria-pressed={chosen}
            className={`format-card ${className}${chosen ? ` ${selectedClassName}` : ''}`}
            onClick={() => onSelect(format)}
        >
            <span className="format-card-badge">{t('booking.s1.selected')}</span>
            <span className="format-card-icon">{icon}</span>
            <span className="format-card-body">
                <span className="format-card-title">{title}</span>
                <span className="format-card-desc">{description}</span>
            </span>
        </button>
    );
}

/** `15 000 ₸`, grouped with a space as the live site writes it, in either language. */
function formatTenge(amount: number): string {
    return `${amount.toLocaleString('ru-RU').replace(/\s/g, ' ')} ₸`;
}
